import type { LotterySiteSettings } from '../types/lottery';
import { resolveUploadUrl } from './uploadUrl';

type ApplySiteMetaOptions = {
  title: string;
  appName?: string;
  appShortName?: string;
  fallbackDescription?: string;
  fallbackKeywords?: string;
};

let manifestObjectUrl = '';

function upsertMeta(name: string, content: string) {
  let node = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!content) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement('meta');
    node.name = name;
    document.head.appendChild(node);
  }
  node.content = content;
}

function upsertLink(rel: string, href: string) {
  let node = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!href) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement('link');
    node.rel = rel;
    document.head.appendChild(node);
  }
  node.href = href;
}

function resolvePublicAsset(path: string) {
  const publicUrl = (process.env.PUBLIC_URL || '').trim();
  if (!publicUrl) {
    return path;
  }
  return `${publicUrl}${path}`;
}

function resolveIconMimeType(src: string) {
  if (src.endsWith('.png')) {
    return 'image/png';
  }
  if (src.endsWith('.svg')) {
    return 'image/svg+xml';
  }
  return 'image/x-icon';
}

function upsertManifest(appName: string, appShortName: string, faviconHref: string) {
  const manifestLink = document.head.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!manifestLink || (!appName && !appShortName)) {
    return;
  }

  const themeColor =
    (document.head.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null)?.content || '#ffffff';
  const iconHref = faviconHref || resolvePublicAsset('/favicon.ico');
  const manifest = {
    short_name: appShortName || appName,
    name: appName || appShortName,
    icons: [
      {
        src: iconHref,
        sizes: '64x64 32x32 24x24 16x16',
        type: resolveIconMimeType(iconHref),
      },
      {
        src: resolvePublicAsset('/logo192.png'),
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: resolvePublicAsset('/logo512.png'),
        sizes: '512x512',
        type: 'image/png',
      },
    ],
    start_url: (process.env.PUBLIC_URL || '').trim() || '.',
    display: 'standalone',
    theme_color: themeColor,
    background_color: '#ffffff',
  };

  if (manifestObjectUrl) {
    URL.revokeObjectURL(manifestObjectUrl);
  }
  manifestObjectUrl = URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }),
  );
  manifestLink.href = manifestObjectUrl;
}

function resolveCanonicalHref(siteUrl?: string) {
  const baseUrl = String(siteUrl || '').trim();
  if (!baseUrl) {
    return '';
  }
  try {
    return new URL(`${window.location.pathname}${window.location.search}`, baseUrl).toString();
  } catch {
    return '';
  }
}

export function applySiteMeta(settings: LotterySiteSettings | null, options: ApplySiteMetaOptions) {
  const description =
    settings?.seo_description?.trim() ||
    settings?.site_description?.trim() ||
    options.fallbackDescription ||
    '';
  const keywords = settings?.seo_keywords?.trim() || options.fallbackKeywords || '';
  const faviconHref = resolveUploadUrl(settings?.site_favicon);
  const canonicalHref = resolveCanonicalHref(settings?.site_url);
  const appName = (options.appName || '').trim();
  const appShortName = (options.appShortName || appName).trim();

  document.title = options.title;
  upsertMeta('description', description);
  upsertMeta('keywords', keywords);
  upsertMeta('application-name', appShortName || appName);
  upsertMeta('apple-mobile-web-app-title', appShortName || appName);
  upsertLink('canonical', canonicalHref);

  if (faviconHref) {
    upsertLink('icon', faviconHref);
    upsertLink('shortcut icon', faviconHref);
    upsertLink('apple-touch-icon', faviconHref);
  }
  upsertManifest(appName, appShortName, faviconHref);
}
